'use client'

export const runtime = 'edge'

import { AlertCircle, Loader2, Lock, User } from 'lucide-react'
import { useState } from 'react'
import { useParams } from 'next/navigation'
import { useLoginRedirect } from '@/hooks/useAuthGuard'
import { useAgentAuth } from '@/stores/auth'
import { Link } from '@/i18n/navigation'
import { LoginFormContainer } from '@/components/login-form/login-form-container'
import { useForm } from 'react-hook-form'
import { Form, FormField } from '@/components/ui/form'
import React from 'react'

interface LoginFormState {
  username: string
  password: string
}

export default function AgentLoginPage() {
  const form = useForm<LoginFormState>({
    defaultValues: {
      username: '',
      password: '',
    },
  })

  const [error, setError] = useState<string | null>(null)
  const params = useParams()
  const locale = (params?.locale as string) || 'en'

  const { login, isLoading } = useAgentAuth()

  // Automatically redirect if already authenticated
  useLoginRedirect('agent')

  const handleSubmit = async (data: LoginFormState) => {
    setError(null)

    const { username, password } = data
    const result = await login(username, password)
    console.log('Login result:', result)
    if (!result.success) {
      setError(result.error || 'Login failed')
    }
    // If successful, the useLoginRedirect hook will handle the redirect
  }

  return (
    <LoginFormContainer
      title="Agent Portal"
      description="Sign in to access the agent dashboard"
    >
      <div className="bg-white py-8 px-6 shadow-lg rounded-lg text-black">
        <Form {...form}>
          <form
            className="mt-8 space-y-6"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => {
                return (
                  <div>
                    <label
                      htmlFor="username"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Username
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        id="username"
                        type="text"
                        required
                        {...field}
                        className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="Enter your username"
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                )
              }}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => {
                return (
                  <div>
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Password
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute pl-3 inset-y-0 left-0 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-gray-400 z-10" />
                      </div>
                      <input
                        id="password"
                        type="password"
                        required
                        className="appearance-none rounded-none relative block w-full px-3 py-2 pl-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="Password"
                        {...field}
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                )
              }}
            />
            <div className="flex justify-end">
              <Link
                href={`/agent/reset-password/request`}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Forgot password?
              </Link>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Login Error
                    </h3>
                    <div className="mt-2 text-sm text-red-700">{error}</div>
                  </div>
                </div>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-600">
                Need help? Contact your administrator
              </p>
            </div>
          </form>
        </Form>
      </div>
    </LoginFormContainer>
  )
}
